<template>
  <ContentCard v-if="isBuyerIntentMcpEnabled" data-testid="reviews-integrations-card">
    <template #header>
      <ContentCardSubheader>
        {{ t('Reviews.integrations.title') }}
      </ContentCardSubheader>
    </template>
    <div class="flex flex-col gap-y-10 py-6">
      <div data-testid="reviews-integration-buyer-intent-mcp">
        <div class="flex flex-wrap items-center gap-2">
          <span :id="`${inputId}-title`" class="text-body-m-bold">{{ t('Reviews.integrations.buyerIntentMcp.title') }}</span>
          <ChipBadge color="success" size="s">{{ t('Reviews.integrations.badgeNew') }}</ChipBadge>
        </div>
        <div class="text-body-s">
          {{ description }}
          <TooltipWrapper top class="inline-flex align-middle">
            <template #trigger>
              <a
                :href="t('Reviews.integrations.buyerIntentMcp.helpLink')"
                target="_blank"
                rel="noopener noreferrer"
                :aria-label="t('Reviews.integrations.buyerIntentMcp.helpTooltip')"
                data-testid="reviews-integration-buyer-intent-mcp-help-link"
                ><CircleHelpIcon class="lucide-icon-s text-icon-subtlest"
              /></a>
            </template>
            <template #content>
              <div>{{ t('Reviews.integrations.buyerIntentMcp.helpTooltip') }}</div>
            </template>
          </TooltipWrapper>
        </div>
        <div class="mt-3 max-w-xl">
          <InputField
            :id="inputId"
            :model-value="BUYER_INTENT_MCP_URL"
            readonly
            :aria-labelledby="`${inputId}-title`"
            data-testid="reviews-integration-buyer-intent-mcp-url"
          >
            <template #prepend>
              <LinkIcon class="lucide-icon-s" />
            </template>
            <template #append>
              <TooltipWrapper top>
                <template #trigger>
                  <a
                    class="state-layer-neutral inline-flex size-8 items-center justify-center rounded-md text-main focus:omr-focus-visible-outline"
                    :href="BUYER_INTENT_MCP_URL"
                    target="_blank"
                    rel="noopener noreferrer"
                    :aria-label="t('Reviews.integrations.openLink')"
                    data-testid="reviews-integration-buyer-intent-mcp-link"
                  >
                    <ExternalLinkIcon class="lucide-icon-s" />
                  </a>
                </template>
                <template #content>
                  <div>{{ t('Reviews.integrations.openLink') }}</div>
                </template>
              </TooltipWrapper>
            </template>
          </InputField>
        </div>
      </div>
    </div>
  </ContentCard>
</template>

<script setup lang="ts">
import { ChipBadge, InputField, TooltipWrapper } from '@ramp106/omrjs-core-ui'
import { CircleHelpIcon, ExternalLinkIcon, LinkIcon } from '@ramp106/omrjs-core-ui/icons'
import { computed, useId } from 'vue'
import { useI18n } from 'vue-i18n'

import { FEATURE_FLAG } from '@/composables/featureFlagCatalog'
import { useFeatureFlag } from '@/composables/useFeatureFlag'
import ContentCard from '@/ui/ContentCard.vue'
import ContentCardSubheader from '@/ui/ContentCardSubheader.vue'

const BUYER_INTENT_MCP_URL = 'https://mcp.reviews.omr.com/mcp'

const props = defineProps<{
  entityType: 'tool' | 'agency'
}>()

const { t } = useI18n()
const inputId = useId()
const { enabled: isBuyerIntentMcpEnabled } = useFeatureFlag(FEATURE_FLAG.REVIEWS_INTEGRATIONS.BUYER_INTENT_MCP_RELEASE)

const description = computed(() =>
  props.entityType === 'agency'
    ? t('Reviews.integrations.buyerIntentMcp.descriptionAgency')
    : t('Reviews.integrations.buyerIntentMcp.descriptionTool'),
)
</script>
